import { useEffect, useState, useSyncExternalStore } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  buildEntraStartUrl,
  checkAuthenticated,
  isElectronLoginRuntime,
  parseEntraLoginError,
} from "@/lib/entraLogin";
import { LogoutNotice } from "@/pages/auth/LogoutNotice";
import { resolvePostLoginHomeFromLoginPage } from "@/lib/designPreview";
import {
  decodeReturnToParam,
  persistPendingAuftragHandoff,
} from "@/lib/postLoginRedirect";
import { SERVER_UPDATE_MESSAGE } from "@/features/serverUpdate/maintenanceStatus";
import {
  getServerUpdateServerSnapshot,
  getServerUpdateSnapshot,
  subscribeServerUpdate,
} from "@/features/serverUpdate/serverUpdateStore";

const ERNSTHAUS_LOGO = `/static/${encodeURIComponent("Ernsthaus Logo.png")}`;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoutFlow = searchParams.get("logout") === "1";
  const returnToParam = searchParams.get("returnTo");

  const updating = useSyncExternalStore(
    subscribeServerUpdate,
    () => getServerUpdateSnapshot().active,
    () => getServerUpdateServerSnapshot().active,
  );
  const [phase, setPhase] = useState<"checking" | "ready">("checking");
  const [error, setError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginHint, setLoginHint] = useState("Mit Microsoft anmelden");
  const [logoutCountdown, setLogoutCountdown] = useState(6);

  useEffect(() => {
    const loginError = parseEntraLoginError(searchParams.toString());
    if (loginError) setError(loginError);
  }, [searchParams]);

  useEffect(() => {
    const returnPath = decodeReturnToParam(returnToParam);
    if (!returnPath) return;
    const query = returnPath.includes("?") ? returnPath.split("?").slice(1).join("?") : "";
    const handoffToken = new URLSearchParams(query).get("auftrag_handoff")?.trim();
    if (handoffToken) persistPendingAuftragHandoff(handoffToken);
  }, [returnToParam]);

  useEffect(() => {
    if (!logoutFlow) return;
    setLogoutCountdown(6);
    const timer = window.setInterval(() => {
      setLogoutCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          navigate("/login", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [logoutFlow, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (logoutFlow) {
        if (!cancelled) setPhase("ready");
        return;
      }
      const authed = await checkAuthenticated();
      if (cancelled) return;
      if (authed) {
        const targetPath = decodeReturnToParam(returnToParam) || "/";
        if (targetPath.startsWith("/alt")) {
          window.location.replace(`${window.location.origin}${targetPath}`);
          return;
        }
        navigate(targetPath, { replace: true });
        return;
      }
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [logoutFlow, navigate, returnToParam]);

  useEffect(() => {
    if (!updating || logoutFlow) return;
    const id = window.setInterval(() => {
      void checkAuthenticated().then((authed) => {
        if (!authed) return;
        const targetPath = decodeReturnToParam(returnToParam) || "/";
        if (targetPath.startsWith("/alt")) {
          window.location.replace(`${window.location.origin}${targetPath}`);
          return;
        }
        navigate(targetPath, { replace: true });
      });
    }, 3000);
    return () => window.clearInterval(id);
  }, [logoutFlow, navigate, returnToParam, updating]);

  async function handleEntraLogin() {
    // Nur relativen Pfad übergeben – buildEntraStartUrl setzt origin selbst.
    const startUrl = buildEntraStartUrl(
      decodeReturnToParam(returnToParam) || resolvePostLoginHomeFromLoginPage(),
    );
    if (isElectronLoginRuntime()) {
      setLoginLoading(true);
      try {
        const ok = await window.envAPI!.openExternalLogin!(startUrl);
        if (!ok) {
          setLoginLoading(false);
          setError("Login konnte im Browser nicht geöffnet werden.");
          return;
        }
        setLoginHint("Bitte im Browser anmelden – Fenster bleibt offen …");
      } catch (err) {
        setLoginLoading(false);
        setError("Login konnte im Browser nicht gestartet werden.");
        console.error("openExternalLogin failed", err);
      }
      return;
    }

    setLoginLoading(true);
    window.location.href = startUrl;
  }

  function skipLogoutCountdown() {
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-grouped px-4 py-8 text-label">
      <main
        className={`w-full rounded-ios-lg bg-card shadow-ios-md ${
          logoutFlow ? "max-w-[420px] p-8 sm:p-10" : "max-w-[360px] p-6 sm:p-8"
        }`}
      >
        <header className={`text-center ${logoutFlow ? "mb-2" : "mb-6"}`}>
          <img
            src={ERNSTHAUS_LOGO}
            alt="Ernsthaus"
            className="mx-auto mb-4 h-12 w-auto max-w-[200px] object-contain"
            width={200}
            height={48}
            loading="eager"
            decoding="async"
          />
          {!logoutFlow ? (
            <>
              <h1 className="text-xl font-semibold text-label">Ernsthaus - App</h1>
              {updating ? null : (
                <p className="mt-1 text-sm text-label-secondary">
                  Bitte melde dich mit deinem Microsoft-Konto an.
                </p>
              )}
            </>
          ) : null}
        </header>

        {error ? (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        ) : null}

        {logoutFlow ? (
          <LogoutNotice countdown={logoutCountdown} onContinue={skipLogoutCountdown} />
        ) : null}

        {phase === "checking" ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-label-secondary">
            <Spinner />
            <span>Session wird geprüft …</span>
          </div>
        ) : null}

        {phase === "ready" && !logoutFlow && updating ? (
          <div className="flex items-center justify-center gap-2 py-4 text-center text-sm text-label-secondary">
            <Spinner />
            <span>{SERVER_UPDATE_MESSAGE}</span>
          </div>
        ) : null}

        {phase === "ready" && !logoutFlow && !updating ? (
          <Button
            type="button"
            className="w-full"
            size="lg"
            loading={loginLoading}
            onClick={() => void handleEntraLogin()}
          >
            {loginHint}
          </Button>
        ) : null}

        {!logoutFlow ? (
          <footer className="mt-6 text-center text-xs text-label-tertiary">
            Zugriff nur für Mitarbeitende der Gebr. Ernsthaus GmbH.
          </footer>
        ) : null}
      </main>
    </div>
  );
}
